
import Vue from 'vue';
import { formatToMarketSpecificCurrency, SupportedMarket } from '@workbench/core';
import WKOMixin from '@owc-utils/Mixins/WKO';
import { AUTOMATIC_PRICE_ENDPOINT } from './StagePrice.config';
import { StageAutomaticPriceResponseStatusCode, StageStartingPriceMethod } from './StagePrice.enums';
import { createAutomaticPriceLogger } from './StagePrice.utils';
import { StagePriceComponent } from './StagePrice.d';

const logIfAutomaticPriceDebugEnabled = createAutomaticPriceLogger();

export default (Vue as StagePriceComponent).extend({
  name: 'owc-stage-price',
  mixins: [WKOMixin],
  props: {
    priceData: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    market: '',
    language: '',
    price: null,
  }),
  mounted() {
    if (this.isAutomaticStartingPrice) {
      logIfAutomaticPriceDebugEnabled('Automatic price enabled');
      this.onWKOChange(this.loadAndSetAutomaticStartingPrice);
    } else {
      logIfAutomaticPriceDebugEnabled('Automatic price disabled (fallback to the author-defined price)');
      this.price = this.basePrice;
    }
  },
  computed: {
    basePrice() {
      const { headline, priceWithCurrency, pricePrefix, priceHint, tooltip } = this.priceData;
      return {
        headline,
        priceWithCurrency,
        pricePrefix,
        priceHint,
        tooltip,
      };
    },
    isAutomaticStartingPrice() {
      return this.priceData.startingPriceMethod === StageStartingPriceMethod.Automated;
    },
  },
  methods: {
    async loadAndSetAutomaticStartingPrice(state) {
      const locale = state.Locale;
      const country = locale?.country;
      const language = locale?.language;
      const vehicleData = state.Environment?.vehicleData;
      const modelSeries = vehicleData?.modelSeries;

      logIfAutomaticPriceDebugEnabled('Getting data from OWC_WKO seamless', state);

      if (modelSeries && country && language) {
        this.market = country;
        this.language = language;
        try {
          const url = this.createAutomaticPriceURL(modelSeries, vehicleData?.subBrands as string[]);
          logIfAutomaticPriceDebugEnabled(`Loading price from the URL: ${url}`);
          const response = await fetch(url);
          const data = await response.json();
          logIfAutomaticPriceDebugEnabled('Price loaded', data);
          this.processAutomaticPriceResponse(data);
        } catch (error) {
          this.price = this.basePrice;
          logIfAutomaticPriceDebugEnabled((error as any).message, '(fallback to the author-defined price)');
        }
      } else {
        this.price = this.basePrice;
        logIfAutomaticPriceDebugEnabled(
          'Cannot get necessary data from OWC_WKO seamless (fallback to the author-defined price)',
        );
      }
    },
    createAutomaticPriceURL(modelSeries, subBrands = []) {
      const url = new URL(`${window.location.origin}${AUTOMATIC_PRICE_ENDPOINT}`);

      url.searchParams.append('market', this.market);
      url.searchParams.append('language', this.language);
      url.searchParams.append('modelSeries', modelSeries);

      // Append first sub brand from the array
      if (subBrands.length) {
        url.searchParams.append('subBrands', subBrands[0]);
      }

      return url.toString();
    },
    processAutomaticPriceResponse(response) {
      const { status, price } = response;

      if (status !== StageAutomaticPriceResponseStatusCode.Success) {
        throw {
          status,
          message: response.message,
        };
      }

      this.price = {
        ...this.basePrice,
        priceWithCurrency: this.formatPrice(price.min),
        pricePrefix: response.i18n.label,
        tooltip: {
          ...this.basePrice.tooltip,
          text: response.i18n.footnote || '',
        },
      };
    },
    formatPrice(price) {
      const marketAndLanguageCode = `${this.language}-${this.market}`;
      return formatToMarketSpecificCurrency(price, marketAndLanguageCode as SupportedMarket);
    },
  },
});
